import { useLayoutEffect, useState, type RefObject } from 'react';

let measuringContext: CanvasRenderingContext2D | null | undefined;

/** The width `text` takes in the field's own font, or null where nothing can measure it. */
function textWidth(text: string, style: CSSStyleDeclaration): number | null {
  if (measuringContext === undefined) {
    measuringContext =
      typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  }
  if (!measuringContext) return null;
  measuringContext.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return measuringContext.measureText(text).width;
}

/**
 * The room a search field's own clear button takes from its text, in the field's own text size:
 * Chromium and WebKit keep its box (one em and a hairline) while the field is empty. A view's Search
 * showed `"a phrase'`, the closing quote cut (I-1300).
 */
const NATIVE_CLEAR_BUTTON_EMS = 1.25;

/**
 * I-0876: a single-line field's placeholder, whole. A field cannot wrap, so where the full wording
 * does not fit — the search field at the largest text size on a phone, which showed "Searc" — the
 * field says the short one instead. Measured against the room between the field's paddings, again
 * whenever the field's box changes: the text size moves its height, a query moves its padding.
 */
export function useFittingPlaceholder(
  ref: RefObject<HTMLInputElement | null>,
  full: string,
  short: string,
  /** The field is a search field that keeps the browser's own clear button, which takes room. */
  keepsNativeClear = false,
): string {
  const [placeholder, setPlaceholder] = useState(full);
  useLayoutEffect(() => {
    const field = ref.current;
    if (!field) return;
    const fit = () => {
      const style = getComputedStyle(field);
      const room =
        field.clientWidth -
        parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight) -
        (keepsNativeClear ? parseFloat(style.fontSize) * NATIVE_CLEAR_BUTTON_EMS : 0);
      const needed = textWidth(full, style);
      setPlaceholder(needed === null || needed <= room ? full : short);
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(field);
    return () => observer.disconnect();
  }, [ref, full, short, keepsNativeClear]);
  return placeholder;
}
